import styled from "styled-components";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { COLORS } from "../../constants";
import DoNotDisturbIcon from "@mui/icons-material/DoNotDisturb";
import {
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { selectStoreHasInPrice } from "../../redux/storeDataSlice";
import { FORMATTER, isAssemblyProduct } from "../../services";
import {
  selectMenyvelgerenLoading,
  selectMenyvelgerenProducts,
} from "../../redux/menyvelgerenDataSlice";
import { useEffect, useState } from "react";
import { setMenuPosition } from "../../redux/menuPositionSlice";
import { useTranslation } from "react-i18next";
import {
  AddProductToCartProvider,
  useAddProductToCart,
} from "./AddProductToCart";

const StyledMenuProductHeader = styled.div`
  padding: 10px;
  border-bottom: 1px solid ${COLORS.PRIMARY};

  .menu-item-header {
    position: relative;
    font-size: 22px;
    color ${COLORS.PRIMARY};
    line-height: 40px;
  }

  @media only screen and (max-width: 899px) {
    .menu-item-header {
      line-height: 50px;
    }
  }
`;

const StyledMenuProductList = styled.div`
  margin-bottom: 35px;

  &:first-of-type {
    border-top: 5px solid ${COLORS.SECONDARY};
  }
`;

const MenuProductDescriptionCard = (props) => {
  const { description } = props;
  const getFirstLine = (text) => {
    if (text) {
      return text.split("\n")[0];
    }

    return text;
  };

  const getNextLines = (text) => {
    if (text) {
      var q = text.split("\n");
      if (q.length > 1) {
        return q.slice(1).map((line, idx) => {
          return <p key={`description_line_${idx}`}>{line}</p>;
        });
      }
    }

    return [];
  };

  return (
    <Card
      sx={{ marginTop: "10px", borderBottom: `5px solid ${COLORS.SECONDARY}` }}
    >
      <CardContent>
        <Typography variant="h5" component="div">
          {getFirstLine(description)}
        </Typography>
        <Typography variant="h5" component="div"></Typography>
        <Typography variant="body2">{getNextLines(description)}</Typography>
      </CardContent>
    </Card>
  );
};

const PriceRenderer = ({ row, isInPrice }) => {
  const [price, setPrice] = useState("");
  const [isMinPrice, setIsMinPrice] = useState(false);

  useEffect(() => {
    const price_key = isInPrice ? "price_in" : "price_out";
    const min_price_key = isInPrice ? "MIN_PRICE_IN" : "MIN_PRICE_OUT";

    if (isAssemblyProduct(row) && row?.tags?.length) {
      const pricetag = row.tags.find((t) => `${t}`.startsWith(min_price_key));
      if (pricetag) {
        const pricecomponent = pricetag.split("=");
        if (pricecomponent.length === 2) {
          setPrice(FORMATTER.formatCurrent(parseInt(pricecomponent[1]) / 100));
          setIsMinPrice(true);
          return;
        }
      }
    }

    setPrice(FORMATTER.formatCurrent(row[price_key]));
  }, [row, isInPrice]);

  return (
    <>
      {isMinPrice ? "fra " : ""}
      {price}
    </>
  );
};

const ProductItemList = ({ products, hideAddButton }) => {
  const { showAddToChartDialog } = useAddProductToCart();
  const { t } = useTranslation();
  const hasInPrice = useSelector(selectStoreHasInPrice);

  return (
    <TableContainer>
      <Table aria-label="simple table">
        <TableHead>
          <TableRow>
            <TableCell>{t("Produkt")}</TableCell>
            <TableCell
              align="right"
              sx={{
                display: {
                  xs: "none",
                  sm: hasInPrice ? "table-cell" : "none",
                },
              }}
            >
              {t("Spis\u00A0inne", {
                interpolation: { escapeValue: false },
              })}
            </TableCell>
            <TableCell
              align="right"
              sx={{
                display: {
                  xs: "none",
                  sm: hasInPrice ? "table-cell" : "none",
                },
              }}
            >
              {t("Ta\u00A0med")}
            </TableCell>
            <TableCell
              align="right"
              sx={{
                display: {
                  xs: "table-cell",
                  sm: hasInPrice ? "none" : "table-cell",
                },
              }}
            >
              {t("Pris")}
            </TableCell>
            {!hideAddButton && (
              <TableCell align="right">{t("Legg\u00A0til")}</TableCell>
            )}
          </TableRow>
        </TableHead>
        <TableBody>
          {(products || []).map((row) => (
            <TableRow
              key={row.pid}
              sx={{ "&:last-child td, &:last-child th": { border: 0 } }}
            >
              <TableCell
                component="th"
                scope="row"
                sx={{ cursor: "pointer" }}
                onClick={() => showAddToChartDialog(row)}
              >
                <b>{row.title}</b>
                <br />
                {row.description}
              </TableCell>
              <TableCell
                align="right"
                onClick={() => showAddToChartDialog(row)}
                sx={{
                  display: {
                    xs: "none",
                    sm: hasInPrice ? "table-cell" : "none",
                  },
                }}
              >
                <PriceRenderer row={row} isInPrice={true} />
              </TableCell>
              <TableCell
                align="right"
                onClick={() => showAddToChartDialog(row)}
              >
                <PriceRenderer row={row} isInPrice={false} />
              </TableCell>
              {!hideAddButton && (
                <TableCell
                  onClick={() => showAddToChartDialog(row)}
                  align="right"
                >
                  {row.disabled === "1" && (
                    <>
                      <Tooltip
                        title={t("Produktet er midlertidig utilgjengelig")}
                      >
                        <IconButton
                          aria-label={t(
                            "Produktet er midlertidig utilgjengelig"
                          )}
                          color="secondary"
                          onClick={() => showAddToChartDialog(row)}
                        >
                          <DoNotDisturbIcon />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                  {row.disabled !== "1" && (
                    <>
                      <IconButton
                        aria-label={t("Legg til i handlekurv")}
                        color="primary"
                        onClick={() => showAddToChartDialog(row)}
                      >
                        <AddCircleIcon />
                      </IconButton>
                    </>
                  )}
                </TableCell>
              )}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

const MenuProductList = ({ menu, hideAddButton }) => {
  const dispatch = useDispatch();
  const [myPosition, setPosition] = useState(0);
  const { title, mid: menuId, description } = menu || {};
  const products = useSelector((state) =>
    selectMenyvelgerenProducts(state, menuId)
  );
  const loading = useSelector(selectMenyvelgerenLoading);

  return (
    <AddProductToCartProvider>
      <StyledMenuProductList
        ref={(el) => {
          if (!el) return;

          const position = el.getBoundingClientRect();
          const topPosition = position.y + window.scrollY;
          const bottonPosition =
            topPosition + position.height - Math.max(window.innerHeight / 3, 0);

          if (myPosition !== bottonPosition) {
            setPosition(bottonPosition);
            dispatch(
              setMenuPosition({
                menu: menuId,
                position: {
                  top: topPosition,
                  bottom: bottonPosition,
                },
              })
            );
          }
        }}
      >
        <StyledMenuProductHeader>
          <div className="menu-item-header" aria-level={2}>
            {title}
          </div>
        </StyledMenuProductHeader>

        {description && (
          <MenuProductDescriptionCard description={description} />
        )}

        {loading && <LinearProgress color="secondary" />}
        {!loading && (
          <ProductItemList
            menu={menu}
            products={products}
            hideAddButton={hideAddButton}
          />
        )}
      </StyledMenuProductList>
    </AddProductToCartProvider>
  );
};

export default MenuProductList;
